import dateFormat from 'dateformat';

export const mergeSwapDevices = (swapData, devices) => (
  swapData.devices.reduce((eligibleDevices, swapDevice) => {
    const eligibleDevice = devices.find(device => (
      device.serialNumber
      && device.serialNumber === swapDevice.serialNumber
    ));
    if (eligibleDevice) {
      eligibleDevices.push({
        ...swapDevice,
        ...eligibleDevice,
      });
    }

    return eligibleDevices;
  }, [])
);

export const getEligibleDevices = (swapData, devices) => (
  mergeSwapDevices(swapData, devices)
    .filter(({ inFlightOrder }) => !inFlightOrder)
);

export const requiresCallback = (devices, sikEligible) => !sikEligible || devices.length > 4;

const titlecase = (s = '') => s.toString().toLowerCase().replace(/\b([a-z])/g, ch => ch.toUpperCase());

export const formatAddress = ({
  addressLine1,
  addressLine2,
  city,
  state,
  zip,
  zip4,
}) => (
  `${titlecase(addressLine1)}${addressLine2 ? ` ${titlecase(addressLine2)}` : ''}\n${titlecase(city)}, ${state} ${zip} ${zip4 || ''}`.trim()
);

const getShippingAddress = ({ serviceAddress }) => ({
  label: 'Shipping address',
  value: formatAddress(serviceAddress),
  isTranslationExempt: true,
});

const addDays = (date, days) => {
  const parsedDate = new Date(date);
  return parsedDate.setDate(parsedDate.getDate() + days);
};

const getShippingInfo = ({ orderTimestamp }) => {
  const deliveryStart = addDays(orderTimestamp, 3);
  const deliveryEnd = addDays(orderTimestamp, 5);
  return {
    label: 'Free standard shipping',
    value: `Your order will arrive between ${dateFormat(deliveryStart, 'mmmm d', true)} and ${dateFormat(deliveryEnd, 'mmmm d, yyyy', true)}`,
  };
};

const getContactInfo = ({ preferredEmail, mobilePhoneNumber }) => {
  const details = [preferredEmail];

  if (mobilePhoneNumber) {
    details.push(mobilePhoneNumber);
  }

  return details;
};

export const getReviewDetails = (
  account,
  macaroon,
  order = { orderTimestamp: new Date() }, // Set default date as today
) => ([
  getShippingAddress(account),
  getShippingInfo(order),
  {
    label: 'Confirmation and updates will be sent to',
    value: getContactInfo(macaroon),
    isTranslationExempt: true,
  },
]);

export const getConfirmationDetails = (
  account,
  macaroon,
  order = { orderTimestamp: new Date() }, // Set default date as today
) => ([
  {
    label: 'Estimated arrival',
    value: 'Your equipment should arrive within 3-5 days.',
  },
  getShippingAddress(account),
  getShippingInfo(order),
  {
    label: 'Updates will be sent to',
    value: getContactInfo(macaroon),
    isTranslationExempt: true,
  },
]);

const isInvalidDate = date => isNaN(date) || date.length !== 8 || date === '00000000';

const formatToIsoDate = (date) => {
  const year = date.substring(0, 4);
  const month = date.substring(4, 6);
  const day = date.substring(6, 8);
  return `${year}-${month}-${day}T00:00:00`;
};

/* filter orders with orderDate greater than 90 days or shipmentDate
  greater than 30 days if order status is closed */
export const filterOrders = (orders) => {
  const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
  const NINETY_DAYS = 90 * 24 * 60 * 60 * 1000;
  if (orders?.length > 0) {
    const filteredOrders = orders.filter((order) => {
      if (isInvalidDate(order.orderDate) || isInvalidDate(order.shipmentDate)) {
        return order;
      }
      const orderDate = new Date(formatToIsoDate(order.orderDate)).getTime();
      const shipmentDate = new Date(formatToIsoDate(order.shipmentDate)).getTime();
      const today = new Date().getTime();
      if (order.orderStatus === 'Closed') {
        if (today - orderDate < NINETY_DAYS && today - shipmentDate < THIRTY_DAYS) {
          return order;
        }
      } else if (today - orderDate < NINETY_DAYS) {
        return order;
      }
      return null;
    });
    return filteredOrders;
  }
  return [];
};
